import React, { useContext, useEffect, useCallback, FunctionComponent } from 'react';

import { getCustomFieldKeys } from '@api/v4/brandfolders/custom_fields';
import { getCustomFieldMaps } from '@api/v4/assets/custom_fields';

import { CustomFieldKeysResponseObject } from '@api/v4/brandfolders/CustomFieldKeysTypes';

import {
  FlatCustomFieldMaps,
  InitialCustomFieldKeysDispatch,
  InitialCustomFieldMapsDataDispatch,
  UpdateCustomFieldsDispatch,
  CreateCustomFieldsDispatch,
  ActionTypes
} from '@components/asset/modal/tabs/edit/EditTabTypes';

import assetModalContext from '@components/asset/modal/tabs/edit/asset_modal_context';

import { BFLoader } from '@components/common/loader/main';
import { Locale } from '@components/common/language_menu/languagesMap';
import AddCustomInput from './addCustomField';
import CustomFieldValue from './CustomFieldValue';

import { CreateCustomFieldParams, CustomFieldMapsResponse } from '@/api/v4/assets/customFieldTypes';

import './styles/_custom_fields.scss';

interface CustomFieldsSectionProps {
  initialData: {
    customFieldKeys: CustomFieldKeysResponseObject[];
    customFieldMaps: FlatCustomFieldMaps[];
    assetKey: string;
  };
  editState: {
    customFieldMaps: FlatCustomFieldMaps[];
    createCustomFieldMaps: CreateCustomFieldParams[];
  };
  ugtLocale: Locale | '';
}

interface CustomFieldsScopedContext {
  state: CustomFieldsSectionProps;
  dispatch: InitialCustomFieldKeysDispatch
    | InitialCustomFieldMapsDataDispatch
    | UpdateCustomFieldsDispatch
    | CreateCustomFieldsDispatch;
}

const CustomFieldsList: FunctionComponent = () => {
  const { state, dispatch }: CustomFieldsScopedContext = useContext(assetModalContext);
  const controlledCustomFieldsEnabled = BFG.brandfolderSettings?.controlled_custom_fields_enabled;

  const getCustomFieldsAsync = useCallback(async (): Promise<void> => {
    const getCustomFieldKeysAsync = async (): Promise<void> => {
      const customFieldKeysParams = {
        order: 'asc',
        sort_by: 'name',
        ugt_locale: state.ugtLocale || undefined
      };
      const options = { params: customFieldKeysParams, resourceKey: BFG.resource.key, resourceType: BFG.resource.type };
      const response = await getCustomFieldKeys(options);
      const initialDispatch = dispatch as InitialCustomFieldKeysDispatch;
      initialDispatch({ type: ActionTypes.FetchCustomFieldKeys, payload: { response } });
    };
    const getCustomFieldMapsAsync = async (): Promise<void> => {
      const customFieldMapsParams = {
        ugt_locale: state.ugtLocale || undefined
      };
      const response = await getCustomFieldMaps({ assetKey: state.initialData.assetKey }, customFieldMapsParams);
      const initialDispatch = dispatch as InitialCustomFieldMapsDataDispatch;
      initialDispatch({ type: ActionTypes.FetchCustomFieldMaps, payload: { response } });
    };
    if (!state.initialData.assetKey) {
      await getCustomFieldKeysAsync();
      // make empty custom field maps response based on keys
      const emptyCustomFieldMaps: CustomFieldMapsResponse = { data: [] };
      const initialDispatch = dispatch as InitialCustomFieldMapsDataDispatch;
      initialDispatch({ type: ActionTypes.FetchCustomFieldMaps, payload: { response: emptyCustomFieldMaps } });
    } else {
      await Promise.all([getCustomFieldKeysAsync(), getCustomFieldMapsAsync()]);
    }
  }, [state.initialData.assetKey, state.ugtLocale, dispatch]);

  useEffect(() => {
    if (state.initialData.customFieldKeys === null
      && state.initialData.customFieldMaps === null
    ) {
      getCustomFieldsAsync();
    }
  }, [
    state.initialData.customFieldKeys,
    state.initialData.customFieldMaps,
    getCustomFieldsAsync
  ]);

  const displayCustomFields = (): React.ReactElement => (
    <div className="asset-modal-edit-custom-fields">
      {
        !controlledCustomFieldsEnabled
        && <AddCustomInput />
      }
      <ul className="custom-field-list">
        {
          // use initialData.customFieldKeys to display all custom field keys
          // use editState.CustomFieldMaps to hold new/edited values
          state.editState.customFieldMaps
            ? state.initialData.customFieldKeys.map((key: CustomFieldKeysResponseObject) => {
              const { name, prioritized } = key.attributes;
              return (
                <li key={`custom-field-row-${key.id}`} className="custom-field-row">
                  <span
                    className={`custom-field-key ${prioritized ? 'prioritized' : ''}`}
                  >
                    {name}
                  </span>
                  <CustomFieldValue
                    controlledCustomFieldsEnabled={controlledCustomFieldsEnabled}
                    customFieldKey={key}
                  />
                </li>
              );
            })
            : null
        }
      </ul>
    </div>
  );

  return (
    <>
      {
        state.initialData.customFieldMaps === null || state.initialData.customFieldKeys == null ? (
          <BFLoader />
        ) : (
          displayCustomFields()
        )
      }
    </>
  );
};

export default CustomFieldsList;
